import { Container, Graphics } from 'pixi.js'
import type { GridRect } from '$lib/types'
import { colours } from '../colours'

export class GridLayer {
  readonly container: Container = new Container()
  private readonly fill: Graphics = new Graphics()
  private readonly lines: Graphics = new Graphics()

  constructor(private cellSize: number) {
    this.container.label = 'grid-layer'
    this.container.addChild(this.fill)
    this.container.addChild(this.lines)
  }

  setCellSize(cellSize: number): void {
    if (cellSize === this.cellSize) return
    this.cellSize = cellSize
  }

  /** Redraw the unlocked-tile background and grid lines. */
  draw(unlockedAreas: GridRect[]): void {
    const c = this.cellSize
    this.fill.clear()
    this.lines.clear()

    if (unlockedAreas.length === 0) return

    const fillColour = colours.unlockedFill()
    const lineColour = colours.gridLine()

    // Filled tiles.
    for (const tile of unlockedAreas) {
      this.fill
        .rect(tile.x * c, tile.y * c, tile.width * c, tile.length * c)
        .fill({ color: fillColour })
    }

    // Per-cell grid lines. Subtle but legible — the alpha (0.45) is a
    // renderer choice, not a theme one; Phase 1's smoke test settled it.
    this.lines.setStrokeStyle({ width: 1, color: lineColour, alpha: 0.45 })
    for (const tile of unlockedAreas) {
      const px = tile.x * c
      const py = tile.y * c
      const w = tile.width * c
      const h = tile.length * c
      // Verticals
      for (let i = 0; i <= tile.width; i++) {
        const x = px + i * c
        this.lines.moveTo(x, py).lineTo(x, py + h)
      }
      // Horizontals
      for (let j = 0; j <= tile.length; j++) {
        const y = py + j * c
        this.lines.moveTo(px, y).lineTo(px + w, y)
      }
    }
    this.lines.stroke()
  }

  destroy(): void {
    this.container.destroy({ children: true })
  }
}
